import React, { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ShieldCheckIcon, GraduationCapIcon, BookOpenIcon, SearchIcon } from 'lucide-react';
import { useFormState } from '../hooks/useFormState';
import { degreeInterestOptions, focusOptions, specializationOptions, getSpecializationsForFocus } from '../data/degrees';
import { hasMatchParams, readMatchParams } from '../utils/matchParams';
interface HeroSectionProps {
  /**
   * `source` tells the page WHICH field the visitor just changed, so it can
   * react to the program choice (the last, decisive step) differently from the
   * earlier degree-level and area-of-study steps.
   */
  onSelectionChange?: (degreeInterest: string, areaOfStudy: string, specialization: string, source?: 'degreeInterest' | 'areaOfStudy' | 'specialization' | 'submit') => void;
}
export function HeroSection({
  onSelectionChange
}: HeroSectionProps) {
  const navigate = useNavigate();
  // Clean-URL form: /degree/:areaStudy/:program/:degreeInterest — the third
  // segment is optional.
  const {
    areaStudy,
    program,
    degreeInterest: degreeInterestParam
  } = useParams<{
    areaStudy?: string;
    program?: string;
    degreeInterest?: string;
  }>();
  // Anchor links elsewhere on the site carry the visitor's context in the URL
  // (?degreeInterest=&areaStudy=&mProgram=), so the widget opens pre-filled.
  const urlPrefill = readMatchParams();
  const {
    formData,
    errors,
    updateFormData,
    updateMultiple,
    validateHeroForm
  } = useFormState(hasMatchParams(urlPrefill) ? urlPrefill : undefined);
  useEffect(() => {
    if (areaStudy && program && !formData.areaOfStudy) {
      updateMultiple({
        areaOfStudy: areaStudy,
        specialization: program,
        // A query string still wins, so ?degreeInterest= can override the path.
        ...(degreeInterestParam && !formData.degreeInterest ? {
          degreeInterest: degreeInterestParam
        } : {})
      });
    }
  }, [areaStudy, program, degreeInterestParam, formData.areaOfStudy, formData.degreeInterest, updateMultiple]);
  const filteredSpecializations = formData.areaOfStudy ? getSpecializationsForFocus(formData.areaOfStudy) : [];
  const handleDegreeInterestChange = (value: string) => {
    updateFormData('degreeInterest', value);
    if (onSelectionChange) {
      onSelectionChange(value, formData.areaOfStudy, formData.specialization, 'degreeInterest');
    }
  };
  const handleAreaOfStudyChange = (value: string) => {
    updateFormData('areaOfStudy', value);
    updateFormData('specialization', '');
    if (onSelectionChange) {
      onSelectionChange(formData.degreeInterest, value, '', 'areaOfStudy');
    }
  };
  const handleSpecializationChange = (value: string) => {
    updateFormData('specialization', value);
    if (onSelectionChange) {
      onSelectionChange(formData.degreeInterest, formData.areaOfStudy, value, 'specialization');
    }
  };
  const handleContinue = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateHeroForm() && onSelectionChange) {
      onSelectionChange(formData.degreeInterest, formData.areaOfStudy, formData.specialization, 'submit');
    }
  };
  // Resolve display labels for trust signals
  const selectedDegree = degreeInterestOptions.find(d => d.value === formData.degreeInterest);
  const selectedFocus = focusOptions.find(f => f.value === formData.areaOfStudy);
  const selectedSpecOption = specializationOptions.find(s => s.value === formData.specialization);
  const selectedSpecName = selectedSpecOption?.type;
  // --- H1 headline logic ---
  const currentYear = new Date().getFullYear();
  const searchParams = new URLSearchParams(window.location.search);
  const t10verride = searchParams.get('t10verride');
  const smallWords = new Set(['for', 'in', 'of', 'a', 'an', 'the', 'and', '&']);
  function capitalizeHeadline(text: string): string {
    return text.split(' ').map((word, idx) => {
      if (idx === 0) return word.charAt(0).toUpperCase() + word.slice(1);
      if (smallWords.has(word.toLowerCase())) return word.toLowerCase();
      return word.charAt(0).toUpperCase() + word.slice(1);
    }).join(' ');
  }
  const degreeNameMap: Record<string, string> = {
    '1': 'Certificate',
    '2': "Associate's",
    '3': "Bachelor's",
    '4': "Master's"
  };
  const hasSpecificDegree = ['1', '2', '3', '4'].includes(formData.degreeInterest);
  const degreeName = degreeNameMap[formData.degreeInterest] || '';
  const isGeneralArea = formData.areaOfStudy === '9';
  const isGeneralProgram = !selectedSpecName || /^(General|All )/i.test(selectedSpecName);
  const areaName = selectedFocus?.type || '';
  function buildHeadline(): string {
    if (hasSpecificDegree) {
      if (!isGeneralProgram && selectedSpecName) {
        // Case 1: Specific degree + specific program
        return `Best Online ${degreeName} Degree in ${selectedSpecName} of ${currentYear}`;
      }
      if (isGeneralArea) {
        // Case 2: Specific degree + General area
        return `Best Colleges for a ${degreeName} Degree Program in ${currentYear}`;
      }
      // Case 3: Specific degree + specific area but general/all program
      return `Best Online ${degreeName} Degree in ${areaName} of ${currentYear}`;
    }
    // No specific degree selected
    if (isGeneralArea || !formData.areaOfStudy && !formData.specialization) {
      // Case 4a: default landing
      return `Top Accredited Degree Programs of ${currentYear}`;
    }
    if (isGeneralProgram || !selectedSpecName) {
      // Case 4b: No degree + specific area or "General" program
      return `Best Colleges for an Online ${areaName} Degree of ${currentYear}`;
    }
    // Case 4c: No degree + specific program
    return `Best Colleges for an Online ${selectedSpecName} Degree of ${currentYear}`;
  }
  let headline = capitalizeHeadline(buildHeadline());
  // Apply t10verride if present
  if (t10verride) {
    headline = headline.replace(/^Top Accredited/i, t10verride).replace(/^Best Online/i, t10verride).replace(/^Best Colleges for/i, t10verride);
  } else {
    // Lead with the action. "Best/Top ..." takes an article; anything else
    // reads correctly straight after the verb.
    headline = /^Best /i.test(headline) ? `Find the ${headline}` : `Find ${headline}`;
  }
  return <div className="relative bg-navy overflow-hidden" data-id="element-548" data-mp="wh3vyl9w4qc3">
      {/* Brand background photo. The scrim that keeps the headline legible is
          part of .hero-bg, so every hero band across the site gets it. */}
      <div className="hero-bg absolute inset-0 z-0" aria-hidden="true" data-id="element-549" data-mp="168qhbafy05sk" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-550" data-mp="8qqe1nl0s7v8">
        {/* On mobile the search widget sits directly under the headline, so the
            first thing in reach is the action. On desktop the copy stacks in the
            left column with the widget alongside it. */}
        <div className="py-12 md:py-16 lg:py-20 flex flex-col lg:grid lg:grid-cols-2 lg:items-center lg:gap-x-12" data-id="element-551" data-mp="18a40a957wea">
          {/* Headline */}
          <div className="order-1 lg:col-start-1 lg:row-start-1 text-center lg:text-left" data-id="element-552" data-mp="3neyw495cy32m">
            <div className="inline-flex items-center px-3 py-1 rounded-full bg-white/10 border border-white/15 text-sm font-semibold mb-5 text-white" data-id="element-553" data-mp="3nhbeuoixwhgq">
              <span className="flex h-2 w-2 rounded-full mr-2 bg-gold-light" data-id="element-554" data-mp="3elvbngwaopyh" />
              Fall 2026 Enrollment Open
            </div>

            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-[3.25rem] font-extrabold text-white tracking-tight mb-6 lg:mb-5 leading-[1.15]" data-id="element-555" data-mp="39l7lsyjsyf5u">
              {headline}
            </h1>
          </div>

          {/* Supporting copy + trust row */}
          <div className="order-3 lg:order-none lg:col-start-1 lg:row-start-2 text-center lg:text-left mt-10 lg:mt-0" data-id="element-556" data-mp="2vou5hsr08jsy">
            <p className="text-base md:text-lg text-gray-300 mb-8 max-w-xl mx-auto lg:mx-0 leading-relaxed" data-id="element-557" data-mp="nfm9bc59ka83">
              Compare accredited online colleges and universities. Get matched
              with programs that fit your career goals, schedule, and budget.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4 sm:gap-6 text-sm text-gray-300" data-id="element-558" data-mp="ieyhwpd4hrdc">
              <div className="flex items-center" data-id="element-559" data-mp="4mih7v7p25fl">
                <svg className="w-5 h-5 text-green-400 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-560" data-mp="9gd6de62jcm2">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" data-id="element-561" data-mp="3vvecpvaoajun" />
                </svg>
                Flexible and Convenient
              </div>
              <div className="flex items-center" data-id="element-562" data-mp="339fyky7v5kme">
                <svg className="w-5 h-5 text-green-400 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-563" data-mp="33a97o5uaom7p">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" data-id="element-564" data-mp="379i3dg8wau5w" />
                </svg>
                Accredited Schools
              </div>
              <div className="flex items-center" data-id="element-565" data-mp="3uzkspolfdm0t">
                <svg className="w-5 h-5 text-green-400 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-566" data-mp="3uyariqj9zxpy">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" data-id="element-567" data-mp="8snmjuhcq3zp" />
                </svg>
                No Obligation
              </div>
            </div>
          </div>

          {/* Search widget */}
          <div className="order-2 lg:order-none lg:col-start-2 lg:row-start-1 lg:row-span-2 flex justify-center lg:justify-end" data-id="element-568" data-mp="2i01n5gi458h3">
            <div className="w-full max-w-md" data-id="element-569" data-mp="2d29irn0a7tw0">
              <form onSubmit={handleContinue} className="bg-white rounded-xl shadow-2xl overflow-hidden border border-gray-100" data-id="element-570" data-mp="2czdl7zscj783">
                {/* Card Header */}
                <div className="px-6 py-3.5 bg-navy" style={{
                backgroundColor: "#5FA396"
              }} data-id="element-571" data-mp="30qsru4r5oap5">
                  <h2 className="text-white font-bold text-center text-sm tracking-widest uppercase" data-id="element-572" data-mp="2vrokzkrpep1z">
                    Search Featured Schools
                  </h2>
                </div>

                <div className="p-6 space-y-4" data-id="element-573" data-mp="1p99jxz2mrjk9">
                  {/* Degree Level */}
                  <div data-id="element-574" data-mp="1kem38mdvbtg8">
                    <div className="relative" data-id="element-575" data-mp="1a8m55wv69r80">
                      <GraduationCapIcon className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-navy" aria-hidden="true" data-id="element-576" data-mp="1a9x8xwomayz4" />
                      <select id="degreeInterest" value={formData.degreeInterest} onChange={e => handleDegreeInterestChange(e.target.value)} aria-label="Select Degree Level" className={`block w-full pl-9 pr-3 py-3 text-sm border rounded-md bg-white appearance-none cursor-pointer ${errors.degreeInterest ? 'border-red-400 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-navy focus:border-navy'}`} data-id="element-577" data-mp="1k4p9989l3zxl">
                      <option value="" data-id="element-578" data-mp="8lbpsevogt1a">Select Degree Level</option>
                      {degreeInterestOptions.map(opt => <option key={opt.value} value={opt.value} data-id="element-579" data-mp="5hdyen86bwks">
                          {opt.type}
                        </option>)}
                      </select>
                    </div>
                    {errors.degreeInterest && <p className="mt-1 text-xs text-red-600" data-id="element-580" data-mp="fdqqxhrzg4dy">
                        {errors.degreeInterest}
                      </p>}
                  </div>

                  {/* Area of Study */}
                  <div data-id="element-581" data-mp="jheyi0gm6zz7">
                    <div className="relative" data-id="element-582" data-mp="ji6w1gtrv7s4">
                      <BookOpenIcon className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-navy" aria-hidden="true" data-id="element-583" data-mp="jfcbzqeo7oz8" />
                      <select id="areaOfStudy" value={formData.areaOfStudy} onChange={e => handleAreaOfStudyChange(e.target.value)} aria-label="Select Area of Study" className={`block w-full pl-9 pr-3 py-3 text-sm border rounded-md bg-white appearance-none cursor-pointer ${errors.areaOfStudy ? 'border-red-400 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-navy focus:border-navy'}`} data-id="element-584" data-mp="t9kstguc9thk">
                      <option value="" data-id="element-585" data-mp="1hr1y39sbtztt">Select Area of Study</option>
                      {focusOptions.map(opt => <option key={opt.value} value={opt.value} data-id="element-586" data-mp="131noc0ku9gdw">
                          {opt.type}
                        </option>)}
                      </select>
                    </div>
                    {errors.areaOfStudy && <p className="mt-1 text-xs text-red-600" data-id="element-587" data-mp="y3ccxlewaxtk">
                        {errors.areaOfStudy}
                      </p>}
                  </div>

                  {/* Specialization */}
                  <div data-id="element-588" data-mp="p24ft6o48312">
                    <div className="relative" data-id="element-589" data-mp="p2o18xcz59ju">
                      <SearchIcon className={`pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${formData.areaOfStudy ? 'text-navy' : 'text-gray-400'}`} aria-hidden="true" data-id="element-590" data-mp="1rhdcz73fdzh2" />
                      <select id="specialization" value={formData.specialization} onChange={e => handleSpecializationChange(e.target.value)} disabled={!formData.areaOfStudy} aria-label="Select Program" className={`block w-full pl-9 pr-3 py-3 text-sm border rounded-md bg-white appearance-none cursor-pointer disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed ${errors.specialization ? 'border-red-400 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-navy focus:border-navy'}`} data-id="element-591" data-mp="1im73ny4y4z9s">
                      <option value="" data-id="element-592" data-mp="2woel3yqn17ft">
                        {formData.areaOfStudy ? 'Select Program' : 'Select an area of study first'}
                      </option>
                      {filteredSpecializations.map(opt => <option key={opt.value} value={opt.value} data-id="element-593" data-mp="3bd1c4xpmnnmv">
                          {opt.type}
                        </option>)}
                      </select>
                    </div>
                    {errors.specialization && <p className="mt-1 text-xs text-red-600" data-id="element-594" data-mp="2iom08k1sllfr">
                        {errors.specialization}
                      </p>}
                  </div>

                  {/* Continue Button */}
                  <button type="submit" className="w-full bg-cta hover:bg-cta-dark text-white font-bold py-3.5 px-6 rounded-md text-sm tracking-wider uppercase transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cta-dark" style={{
                  backgroundColor: "#E3924E"
                }} data-id="element-595" data-mp="2rmpc0omz73ud">
                    Search Programs
                  </button>
                </div>

                {/* Reassurance footer — answers the three things people
                    hesitate over right before submitting. */}
                <div className="border-t border-gray-200 bg-light px-6 py-4" data-id="element-596" data-mp="280ix1aewbaa9">
                  <p className="flex items-center justify-center gap-2 text-xs font-semibold text-navy" data-id="element-597" data-mp="1kbg4v8mmxz1i">
                    <ShieldCheckIcon className="w-4 h-4 flex-shrink-0 text-gold-dark" aria-hidden="true" data-id="element-598" data-mp="1ga4ntrhtum4x" />
                    Trusted by 2M+ students to find the right school
                  </p>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>;
}